import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table, DatePicker, Modal, Alert, Button, Space, Grid, Tag, Tooltip, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { raporlamaAPI } from '../services/api';
import { formatPara } from '../utils/format';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const GELIR_RENK = '#3f8600';
const GIDER_RENK = '#cf1322';

const ayaCevir = (d) => d.format('YYYY-MM');
const ayEtiketi = (ay) => dayjs(`${ay}-01`).format('MM.YYYY');

// Bir hücre: tıklanabilirse detay modalını açar, sıfırsa "-" gösterir
function Hucre({ tutar, renk, kalin, onClick }) {
  const deger = Number(tutar || 0);
  if (!deger) return <span style={{ color: '#bfbfbf' }}>-</span>;
  const icerik = <span style={{ color: renk, fontWeight: kalin ? 600 : 400 }}>{formatPara(deger)}</span>;
  if (!onClick) return icerik;
  return <a onClick={onClick} style={{ textDecoration: 'none' }}>{icerik}</a>;
}

function GelirGiderTablosu() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  // Varsayılan: içinde bulunulan ay ve öncesi 2 ay (3 aylık pencere)
  const [aralik, setAralik] = useState([dayjs().subtract(2, 'month').startOf('month'), dayjs().startOf('month')]);
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detay, setDetay] = useState({ open: false, loading: false, baslik: '', tur: 'gelir', kayitlar: [], toplam: 0 });

  const fetchRapor = useCallback(async (bas, bit) => {
    setLoading(true);
    try {
      const res = await raporlamaAPI.gelirGiderAylik(ayaCevir(bas), ayaCevir(bit));
      setRapor(res.data);
    } catch (err) {
      message.error('Gelir/gider raporu yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRapor(aralik[0], aralik[1]); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAralikDegisti = (degerler) => {
    if (!degerler || !degerler[0] || !degerler[1]) return;
    const yeni = [degerler[0].startOf('month'), degerler[1].startOf('month')];
    setAralik(yeni);
    fetchRapor(yeni[0], yeni[1]);
  };

  // Hücreye tıklanınca o ayın (veya tüm aralığın) kayıtlarını çek
  const detayAc = async ({ tur, tipId, tipAd, ay }) => {
    const donem = ay ? ayEtiketi(ay) : `${ayEtiketi(rapor.baslangic)} - ${ayEtiketi(rapor.bitis)}`;
    const tipMetni = tipAd || (tur === 'gelir' ? 'Tüm Gelirler' : 'Tüm Giderler');
    setDetay({ open: true, loading: true, baslik: `${tipMetni} — ${donem}`, tur, kayitlar: [], toplam: 0 });
    try {
      const params = { tur };
      if (ay) params.ay = ay;
      else { params.baslangic = rapor.baslangic; params.bitis = rapor.bitis; }
      // tipAd yoksa satır "Toplam Gelir/Gider" özet satırıdır → tip filtresi uygulanmaz
      if (tipAd) params.tip_id = tipId != null ? tipId : 'yok';
      const res = await raporlamaAPI.gelirGiderDetay(params);
      setDetay(d => ({ ...d, loading: false, kayitlar: res.data.kayitlar || [], toplam: res.data.toplam || 0 }));
    } catch (err) {
      setDetay(d => ({ ...d, loading: false }));
      message.error('Kayıtlar yüklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  // Tablo satırları: GELİRLER başlığı + tipler + toplam, GİDERLER başlığı + tipler + toplam, NET
  const satirlar = useMemo(() => {
    if (!rapor) return [];
    const bolum = (tur, veri, baslikMetni, toplamMetni) => {
      const renk = tur === 'gelir' ? GELIR_RENK : GIDER_RENK;
      const tipSatirlari = veri.tipler.map(t => ({
        key: `${tur}-${t.tip_id ?? 'yok'}`,
        tur,
        renk,
        ad: t.tip_ad,
        tipId: t.tip_id,
        tipAd: t.tip_ad,
        adet: t.adet,
        nakit: t.nakit || 0,
        degerler: Object.fromEntries(Object.entries(t.aylar).map(([ay, h]) => [ay, h.tutar])),
        toplam: t.toplam,
      }));
      return [
        { key: `${tur}-baslik`, tip: 'baslik', renk, ad: baslikMetni },
        ...tipSatirlari,
        {
          key: `${tur}-toplam`, tip: 'ozet', tur, renk, ad: toplamMetni,
          tipId: null, tipAd: null,
          degerler: veri.aylik_toplam, toplam: veri.toplam, nakit: veri.nakit || 0,
        },
      ];
    };
    return [
      ...bolum('gelir', rapor.gelir, 'GELİRLER', 'Toplam Gelir'),
      ...bolum('gider', rapor.gider, 'GİDERLER', 'Toplam Gider'),
      { key: 'net', tip: 'net', ad: 'NET (Gelir - Gider)', degerler: rapor.net.aylik_toplam, toplam: rapor.net.toplam },
    ];
  }, [rapor]);

  const columns = useMemo(() => {
    if (!rapor) return [];
    const hucreKolonu = (baslik, ay) => ({
      title: baslik,
      key: ay || 'toplam',
      align: 'right',
      width: ay ? 118 : 130,
      fixed: ay ? undefined : (isMobile ? undefined : 'right'),
      onCell: (r) => (r.tip === 'ozet' || r.tip === 'net' ? { style: { background: '#fafafa' } } : {}),
      render: (_, r) => {
        if (r.tip === 'baslik') return null;
        const deger = ay ? (r.degerler?.[ay] ?? 0) : r.toplam;
        const renk = r.tip === 'net'
          ? (Number(deger) >= 0 ? GELIR_RENK : GIDER_RENK)
          : r.renk;
        const tiklanabilir = r.tip !== 'net' && Number(deger) !== 0;
        return (
          <Hucre
            tutar={deger}
            renk={renk}
            kalin={r.tip === 'ozet' || r.tip === 'net'}
            onClick={tiklanabilir ? () => detayAc({ tur: r.tur, tipId: r.tipId, tipAd: r.tipAd, ay }) : undefined}
          />
        );
      },
    });

    return [
      {
        title: 'Tip',
        dataIndex: 'ad',
        key: 'ad',
        width: isMobile ? 140 : 190,
        fixed: isMobile ? undefined : 'left',
        onCell: (r) => (r.tip === 'ozet' || r.tip === 'net' ? { style: { background: '#fafafa' } } : {}),
        render: (v, r) => {
          if (r.tip === 'baslik') {
            return <span style={{ fontWeight: 700, color: r.renk, letterSpacing: 0.5 }}>{v}</span>;
          }
          if (r.tip === 'ozet' || r.tip === 'net') return <span style={{ fontWeight: 600 }}>{v}</span>;
          return (
            <Space size={6}>
              <span>{v}</span>
              {r.adet ? <Tag style={{ margin: 0 }}>{r.adet}</Tag> : null}
              {r.nakit ? (
                <Tooltip title={`Bunun ${formatPara(r.nakit)} kadarı faturası girilmemiş nakit hareketten geliyor`}>
                  <Tag color="orange" style={{ margin: 0 }}>nakit</Tag>
                </Tooltip>
              ) : null}
            </Space>
          );
        },
      },
      ...rapor.aylar.map(ay => hucreKolonu(ayEtiketi(ay), ay)),
      hucreKolonu('Toplam', null),
    ];
  }, [rapor, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const detayKolonlari = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 90 : 110, render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '-') },
    {
      title: 'Kaynak', dataIndex: 'kaynak', key: 'kaynak', width: 92,
      render: (v) => (v === 'nakit'
        ? <Tooltip title="Faturası girilmemiş nakit hareket (tahsilat/ödeme kaydı)"><Tag color="orange">Nakit</Tag></Tooltip>
        : <Tag color="blue">Fatura</Tag>),
    },
    { title: 'Cari', dataIndex: 'cari', key: 'cari', render: (v) => v || '-' },
    ...(detay.tur === 'gider'
      ? [{ title: 'Fatura No', dataIndex: 'fatura_no', key: 'fatura_no', width: 110, render: (v) => v || '-' }]
      : []),
    { title: 'Tip', dataIndex: 'tip_ad', key: 'tip_ad', width: 130, render: (v) => v || 'Tipsiz' },
    { title: 'Kasa', dataIndex: 'kasa_ad', key: 'kasa_ad', width: 120, render: (v) => v || '-' },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', render: (v) => v || '-' },
    {
      title: 'Tutar', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: isMobile ? 100 : 130, align: 'right',
      render: (v) => <span style={{ color: detay.tur === 'gelir' ? GELIR_RENK : GIDER_RENK }}>{formatPara(v)}</span>,
    },
    {
      title: '', key: 'islem', width: 50,
      render: (_, r) => {
        // Fatura kaydı Satışlar/Alışlar, nakit kaydı Tahsilatlar/Ödemeler ekranında açılır
        const gelirMi = detay.tur === 'gelir';
        const hedef = r.kaynak === 'nakit'
          ? (gelirMi ? '/tahsilatlar' : '/odemeler')
          : (gelirMi ? '/satislar' : '/alislar');
        const etiket = { '/satislar': 'Satışı aç', '/alislar': 'Alışı aç', '/tahsilatlar': 'Tahsilatı aç', '/odemeler': 'Ödemeyi aç' }[hedef];
        return (
          <Button
            size="small"
            icon={<EditOutlined />}
            title={etiket}
            onClick={() => {
              setDetay(d => ({ ...d, open: false }));
              navigate(`${hedef}?duzenle=${r.kayit_id}`);
            }}
          />
        );
      },
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Gelir = satış faturaları + faturası girilmemiş nakit tahsilatlar. Gider = alış fatura kalemleri + faturası girilmemiş nakit ödemeler. Nakit kısım, carisiz ve hiçbir faturaya eşleşmemiş tahsilat/ödemelerden gelir; tip satırındaki turuncu 'nakit' etiketi ne kadarının buradan geldiğini gösterir. Bir hücreye tıklayınca her iki kaynağın kayıtları birlikte listelenir."
      />

      {rapor && (rapor.kapsam_disi?.gelir.adet > 0 || rapor.kapsam_disi?.gider.adet > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={'Rapora GİRMEYEN hareketler: '
            + [
              rapor.kapsam_disi.gelir.adet > 0
                ? `${rapor.kapsam_disi.gelir.adet} tahsilat (${formatPara(rapor.kapsam_disi.gelir.tutar)})`
                : null,
              rapor.kapsam_disi.gider.adet > 0
                ? `${rapor.kapsam_disi.gider.adet} ödeme (${formatPara(rapor.kapsam_disi.gider.tutar)})`
                : null,
            ].filter(Boolean).join(' ve ')
            + '. Gelir/gider tipi atanmadığı için sınıflandırılamıyor — örneğin bir borç tahsilatı nakit girişidir ama ciro değildir. Rapora dahil edilmesini istiyorsanız ilgili kayda tip atayın.'}
        />
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker picker="month" value={aralik} onChange={handleAralikDegisti} format="MM.YYYY" allowClear={false} />
        <Button onClick={() => handleAralikDegisti([dayjs().subtract(2, 'month'), dayjs()])}>Son 3 Ay</Button>
        <Button onClick={() => handleAralikDegisti([dayjs().startOf('year'), dayjs().startOf('month')])}>Bu Yıl</Button>
        <Button onClick={() => handleAralikDegisti([dayjs().subtract(11, 'month'), dayjs()])}>Son 12 Ay</Button>
      </Space>

      {rapor && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card size="small"><Statistic title="Toplam Gelir" value={rapor.gelir.toplam} precision={2} suffix="₺" valueStyle={{ color: GELIR_RENK }} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small"><Statistic title="Toplam Gider" value={rapor.gider.toplam} precision={2} suffix="₺" valueStyle={{ color: GIDER_RENK }} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small"><Statistic title="Net" value={rapor.net.toplam} precision={2} suffix="₺" valueStyle={{ color: rapor.net.toplam >= 0 ? GELIR_RENK : GIDER_RENK, fontWeight: 700 }} /></Card>
          </Col>
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={satirlar}
        rowKey="key"
        loading={loading}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={detay.baslik}
        open={detay.open}
        onCancel={() => setDetay(d => ({ ...d, open: false }))}
        footer={null}
        width={isMobile ? '95%' : 1000}
      >
        <Table
          columns={detayKolonlari}
          dataSource={detay.kayitlar}
          rowKey="id"
          loading={detay.loading}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} / ${t} kayıt` }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={detayKolonlari.length - 2}>
                <b>Toplam ({detay.kayitlar.length} kayıt)</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <b style={{ color: detay.tur === 'gelir' ? GELIR_RENK : GIDER_RENK }}>{formatPara(detay.toplam)}</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
        />
      </Modal>
    </div>
  );
}

export default GelirGiderTablosu;
